import React, { useEffect, useState } from 'react';
import Button from '../Button';
import Unread from '../Unread';
import ResourceDialog from './ResourceDialog';
import Breadcrumb, { FileGlyph, FolderGlyph } from './Breadcrumb';
import { useFiles } from '../../data/useFiles';
import useResourceBrowser from '../../hooks/useResourceBrowser';
import { isLinkable, linkRefusal, resourceMessage } from '../../domain/resources';
import s from './resources.module.css';

// "Link an Online Resource" — the drawer's Existing Online Resources entry
// (#424), MMS's modal of that name, whose action is Link Selected: a
// multi-select over the library. MMS's modal's inside is unrecorded, so this
// is the library page's own browser — the breadcrumb from Home, folders to
// open, files to tick.
//
// The selection survives moving between folders, so files from two folders
// can be linked in one go. A file already on the lesson is shown and not
// selectable; one the lapse sweep emptied is not linkable at all
// (`is_linkable`).
export default function ResourcePicker({
  open, current = [], onLink, onClose, tooManyText = null,
}) {
  const {
    files, folders, loaded, failed,
  } = useFiles();
  const browser = useResourceBrowser({ folders, files });
  const [selected, setSelected] = useState([]);

  useEffect(() => { if (open) setSelected([]); }, [open]);

  const refusal = linkRefusal(current, selected);
  const toggle = (id) => setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const empty = !browser.folders.length && !browser.files.length;

  return (
    <ResourceDialog
      open={open}
      title={resourceMessage('picker_title')}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <span className={s.spacer} />
          <Button
            disabled={!selected.length || Boolean(refusal)}
            onClick={() => { onLink(selected); onClose(); }}
          >
            {resourceMessage('picker_action')}
          </Button>
        </>
      )}
    >
      <Breadcrumb crumbs={browser.crumbs} onOpen={browser.open} />
      <div className={s.options}>
        {browser.folders.map((f) => (
          <button type="button" key={f.id} className={s.option} onClick={() => browser.open(f.id)}>
            <FolderGlyph />
            {f.name}
          </button>
        ))}
        {browser.files.map((r) => {
          const linked = current.includes(r.id);
          const usable = isLinkable(r) && !linked;
          return (
            <label
              key={r.id}
              className={[s.option, usable ? '' : s.optionDisabled].filter(Boolean).join(' ')}
            >
              <input
                type="checkbox"
                disabled={!usable}
                checked={linked || selected.includes(r.id)}
                onChange={() => toggle(r.id)}
              />
              <FileGlyph />
              {r.name}
              {linked ? <span className={s.badge}>{resourceMessage('already_linked')}</span> : null}
            </label>
          );
        })}
        {empty && !loaded ? <Unread failed={failed} /> : empty ? (
          <div className={s.empty}>
            {browser.folderId == null && browser.libraryEmpty
              ? resourceMessage('library_empty')
              : resourceMessage('folder_empty')}
          </div>
        ) : null}
      </div>
      {refusal ? <p className={s.error}>{(refusal.code === 'too_many' && tooManyText) || refusal.message}</p> : null}
    </ResourceDialog>
  );
}
